import { useState } from "react";
import { Pencil } from "lucide-react";

import { api, errMsg } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { useToast } from "../../../context/ToastContext.js";
import { Button } from "../../../components/ui/button.js";
import { Textarea } from "../../../components/ui/textarea.js";
import { Badge } from "../../../components/ui/badge.js";
import { Field, LockedNotice, RatingInput, RatingPill, SectionCard, fmtDate } from "../shared.js";
import type { ReviewDetail } from "../types.js";

export default function YearEndSection({
  review,
  onChanged,
}: {
  review: ReviewDetail;
  onChanged: () => void;
}) {
  const toast = useToast();
  const caps = review.capabilities;
  const ye = review.yearEnd;

  const [employeeComments, setEmployeeComments] = useState(ye.employeeComments ?? "");
  const [managerComments, setManagerComments] = useState(ye.managerComments ?? "");
  // The manager enters a single rating out of 5 (RatingInput caps it at 5).
  const [mgrRating, setMgrRating] = useState<number | null>(ye.mgrFinalRating);
  const [saving, setSaving] = useState(false);

  async function call(path: string, body: unknown, message: string) {
    setSaving(true);
    try {
      await api.put(`/performance/reviews/${review.id}${path}`, body);
      toast.show(message, "success");
      onChanged();
    } catch (e) {
      logError(`YearEndSection${path}`, e);
      toast.show(errMsg(e) ?? "Could not save", "error");
    } finally {
      setSaving(false);
    }
  }

  async function post(path: string, body: unknown, message: string) {
    setSaving(true);
    try {
      await api.post(`/performance/reviews/${review.id}${path}`, body);
      toast.show(message, "success");
      onChanged();
    } catch (e) {
      logError(`YearEndSection${path}`, e);
      toast.show(errMsg(e) ?? "Could not save", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionCard
      collapsible
      title="Year-End Review"
      description="The employee's self-review, the manager's final rating, and the employee's acknowledgement."
      actions={<RatingPill value={caps.canEditFinalManager ? mgrRating : ye.mgrFinalRating} label="Final" />}
    >
      <div className="flex flex-col gap-6">
        {/* ── Employee self-review ── */}
        <div className="rounded-md border border-surface-200 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-[13px] font-bold tracking-wide text-surface-600 uppercase">
              Employee self-review
            </h4>
            <div className="flex items-center gap-2">
              {ye.employeeSubmitted && <Badge tone="info">Submitted</Badge>}
              {caps.canUnlockFinalEmployee && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void post("/final/unlock", { target: "EMP" }, "Unlocked for editing")}
                >
                  <Pencil size={13} /> Edit
                </Button>
              )}
              {caps.canEditFinalEmployee && (
                <Button
                  size="sm"
                  disabled={saving}
                  onClick={() => void call("/final/employee", { comments: employeeComments || null }, "Self-review saved")}
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
              )}
            </div>
          </div>
          {caps.canEditFinalEmployee ? (
            <Textarea
              rows={4}
              value={employeeComments}
              onChange={(e) => setEmployeeComments(e.target.value)}
              placeholder="Your reflection on the year"
            />
          ) : (
            <p className="text-[13px] whitespace-pre-wrap">
              {ye.employeeComments || <span className="text-surface-400">Not written yet.</span>}
            </p>
          )}
          {ye.employeeSubmitted && ye.managerSubmitted && !caps.canEditFinalEmployee && (
            <p className="mt-2 text-[12px] text-surface-400">
              Locked — the manager has submitted the year-end review.
            </p>
          )}
        </div>

        {/* ── Manager attribute ratings ── */}
        <div className="rounded-md border border-surface-200 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-[13px] font-bold tracking-wide text-surface-600 uppercase">
              Manager year-end review
            </h4>
            <div className="flex items-center gap-2">
              {ye.managerSubmitted && <Badge tone="success">Submitted</Badge>}
              {caps.canUnlockFinalManager && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void post("/final/unlock", { target: "MGR" }, "Unlocked for editing")}
                >
                  <Pencil size={13} /> Edit
                </Button>
              )}
              {caps.canEditFinalManager && (
                <Button
                  size="sm"
                  disabled={saving || mgrRating == null}
                  onClick={() =>
                    void call(
                      "/final/manager",
                      { mgrFinalRating: mgrRating, comments: managerComments || null },
                      "Year-end review saved",
                    )
                  }
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
              )}
            </div>
          </div>

          <div className="max-w-[200px]">
            <Field label="Rating (out of 5)">
              {caps.canEditFinalManager ? (
                <RatingInput value={mgrRating} onChange={setMgrRating} />
              ) : (
                <RatingPill value={ye.mgrFinalRating} label="Final" />
              )}
            </Field>
          </div>

          {caps.canEditFinalManager && mgrRating == null && (
            <p className="mt-3 text-[12px] text-surface-400">A rating out of 5 is required before this can be saved.</p>
          )}

          <div className="mt-4">
            <Field label="Manager comments">
              {caps.canEditFinalManager ? (
                <Textarea rows={3} value={managerComments} onChange={(e) => setManagerComments(e.target.value)} />
              ) : (
                <p className="text-[13px] whitespace-pre-wrap">
                  {ye.managerComments || <span className="text-surface-400">—</span>}
                </p>
              )}
            </Field>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
